import { useState } from 'react'
import { theme } from '../theme'
import { Ic } from '../icons'
import { api } from '../api'
import { ModalShell } from './ModalShell'
import { IngredientPicker } from './IngredientPicker'
import { categoryOf, unitsForCategory } from '../units'
import type { MealDetail, RecipeIngredient, IngredientRow } from '../types'

// One editable row. `skipped` keeps a recipe ingredient visible (struck through)
// while excluding it from the saved list; `added` flags a one-off the user
// introduced. ingredient_id===0 means an empty add-row not yet filled in.
type Row = RecipeIngredient & { _uid: number; skipped: boolean; added: boolean }

let __uid = 1
const nextUid = () => __uid++
const round2 = (n: number) => Math.round(n * 100) / 100

type Props = {
  open: boolean
  mealId: number
  // Current effective state of the meal (override list if adjusted, else recipe).
  initial: { servings?: number; note?: string; ingredients: RecipeIngredient[] }
  // The recipe's declared serving count (0 = none); the scaling basis when the
  // meal isn't already serving-adjusted.
  recipeServings: number
  adjusted: boolean
  mobile: boolean
  onClose: () => void
  onSaved: (d: MealDetail) => void
}

export function MealAdjustModal({
  open, mealId, initial, recipeServings, adjusted, mobile, onClose, onSaved,
}: Props) {
  const [rows, setRows] = useState<Row[]>(() =>
    initial.ingredients.map(i => ({ ...i, _uid: nextUid(), skipped: false, added: false })))
  const [servings, setServings] = useState<number>(initial.servings ?? recipeServings ?? 0)
  const [note, setNote] = useState(initial.note ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canScale = servings > 0

  const setRow = (uid: number, patch: Partial<Row>) =>
    setRows(rs => rs.map(r => r._uid === uid ? { ...r, ...patch } : r))
  const addRow = () =>
    setRows(rs => [...rs, {
      _uid: nextUid(), ingredient_id: 0, name: '', default_unit: '',
      quantity: 0, unit: '', optional: false, prep: '', skipped: false, added: true,
    }])

  // Scaling rewrites every quantity by the ratio — quantities are stored
  // literally, so the new serving count just becomes the basis going forward.
  const rescale = (next: number) => {
    if (!canScale || next <= 0 || next === servings) { setServings(Math.max(1, next)); return }
    const factor = next / servings
    setRows(rs => rs.map(r => ({ ...r, quantity: round2(r.quantity * factor) })))
    setServings(next)
  }

  const pickIngredient = (uid: number, row: IngredientRow) =>
    setRow(uid, {
      ingredient_id: row.id, name: row.name,
      default_unit: row.default_unit, unit: row.default_unit || '',
    })

  const save = async () => {
    setError(null)
    const ingredients = rows
      .filter(r => !r.skipped && r.ingredient_id > 0)
      .map(r => ({
        ingredient_id: r.ingredient_id, name: r.name, default_unit: r.default_unit,
        quantity: Number(r.quantity) || 0, unit: r.unit, optional: r.optional,
        prep: r.prep?.trim() || undefined,
      }))
    if (ingredients.length === 0) {
      setError('Keep at least one ingredient — use “Revert to recipe” to clear all changes.')
      return
    }
    for (const i of ingredients) {
      if (!i.unit) { setError(`Pick a unit for ${i.name || 'every ingredient'}.`); return }
    }
    setBusy(true)
    try {
      const d = await api.setMealAdjustments(mealId, {
        servings: canScale && servings !== recipeServings ? servings : null,
        note: note.trim() || null,
        ingredients,
      })
      onSaved(d)
    } catch (e) {
      setError(String(e))
    } finally {
      setBusy(false)
    }
  }

  const revert = async () => {
    setError(null)
    setBusy(true)
    try {
      const d = await api.revertMealAdjustments(mealId)
      onSaved(d)
    } catch (e) {
      setError(String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell open={open} onClose={onClose} zIndex={70}
      panelStyle={{ width: 'min(560px, 100%)', maxHeight: '88dvh' }}>
      <div style={{ padding: '16px 18px', borderBottom: `1px solid ${theme.line}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{
            fontFamily: theme.serif, fontSize: 20, fontWeight: theme.headWeight, color: theme.ink,
          }}>Adjust this meal</div>
          <button onClick={onClose} style={{ all: 'unset', cursor: 'pointer', color: theme.inkSoft, padding: 4 }}>
            <Ic.close width="18" height="18"/>
          </button>
        </div>
        <div style={{ fontFamily: theme.sans, fontSize: 12, color: theme.muted, marginTop: 4 }}>
          Changes apply to this meal only — the recipe stays as is.
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && (
          <div style={{
            padding: '8px 12px', borderRadius: theme.radius,
            background: 'rgba(184,83,57,0.08)', color: theme.accentInk,
            fontFamily: theme.sans, fontSize: 12,
          }}>{error}</div>
        )}

        {/* Servings */}
        <div>
          <Label>Servings</Label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Stepper value={servings} disabled={!canScale} onChange={rescale}/>
            {!canScale && (
              <span style={{ fontFamily: theme.sans, fontSize: 11, color: theme.muted, fontStyle: 'italic' }}>
                This recipe has no serving count, so quantities can't be scaled. Edit them directly.
              </span>
            )}
            {canScale && servings !== recipeServings && recipeServings > 0 && (
              <span style={{ fontFamily: theme.sans, fontSize: 11, color: theme.muted }}>
                recipe makes {recipeServings}
              </span>
            )}
          </div>
        </div>

        {/* Ingredients */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <Label>Ingredients</Label>
            <button onClick={addRow} style={ghostBtn}>
              <Ic.plus width="12" height="12"/> Add
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {rows.map(r => (
              <IngRowCard
                key={r._uid} row={r} mobile={mobile}
                onPick={row => pickIngredient(r._uid, row)}
                onChange={patch => setRow(r._uid, patch)}
                onToggleSkip={() => setRow(r._uid, { skipped: !r.skipped })}
                onRemove={() => setRows(rs => rs.filter(x => x._uid !== r._uid))}
              />
            ))}
          </div>
        </div>

        {/* Note */}
        <div>
          <Label>Note (optional)</Label>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
            placeholder="e.g. use the leftover sauce"
            style={{ ...inputStyle, resize: 'vertical', minHeight: 44, fontFamily: theme.sans }}/>
        </div>
      </div>

      <div style={{
        borderTop: `1px solid ${theme.line}`, padding: '12px 18px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
      }}>
        {adjusted ? (
          <button onClick={revert} disabled={busy} style={{
            all: 'unset', cursor: busy ? 'default' : 'pointer',
            fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
            textTransform: 'uppercase', color: theme.accentInk,
          }}>Revert to recipe</button>
        ) : <span/>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} disabled={busy} style={{
            all: 'unset', cursor: 'pointer', padding: '8px 14px', borderRadius: theme.radius,
            fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
            textTransform: 'uppercase', color: theme.ink, border: `1px solid ${theme.lineStrong}`,
          }}>Cancel</button>
          <button onClick={save} disabled={busy} style={{
            all: 'unset', cursor: busy ? 'default' : 'pointer', padding: '8px 14px', borderRadius: theme.radius,
            fontFamily: theme.sans, fontSize: 12, fontWeight: 700, letterSpacing: 0.4,
            textTransform: 'uppercase', color: theme.surface, background: theme.ink,
            opacity: busy ? 0.5 : 1,
          }}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </ModalShell>
  )
}

function IngRowCard({
  row, mobile, onPick, onChange, onToggleSkip, onRemove,
}: {
  row: Row
  mobile: boolean
  onPick: (row: IngredientRow) => void
  onChange: (patch: Partial<Row>) => void
  onToggleSkip: () => void
  onRemove: () => void
}) {
  const unitOpts = unitsForCategory(categoryOf(row.default_unit))
  return (
    <div style={{
      display: 'grid', gap: 8, padding: 10,
      border: `1px solid ${theme.lineStrong}`, borderRadius: theme.radius,
      background: theme.bg, opacity: row.skipped ? 0.55 : 1,
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 8, alignItems: 'center', minWidth: 0 }}>
        <div style={{ minWidth: 0, textDecoration: row.skipped ? 'line-through' : 'none' }}>
          <IngredientPicker
            value={row.ingredient_id ? { ingredient_id: row.ingredient_id, name: row.name, default_unit: row.default_unit } : null}
            onChange={onPick}
            onCreateRequest={() => {}}
            placeholder="Find an ingredient"
          />
        </div>
        {row.added ? (
          <button onClick={onRemove} title="Remove" style={iconBtn}><Ic.trash width="14" height="14"/></button>
        ) : (
          <button onClick={onToggleSkip} title={row.skipped ? 'Keep' : 'Skip for this meal'} style={{
            ...ghostBtn, color: row.skipped ? theme.ink : theme.accentInk,
          }}>{row.skipped ? 'Keep' : 'Skip'}</button>
        )}
      </div>
      {!row.skipped && (
        <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr 1fr' : '90px 100px 1fr auto', gap: 8, alignItems: 'center' }}>
          <input placeholder="Qty" type="number" step="any" value={row.quantity || ''}
            onChange={e => onChange({ quantity: Number(e.target.value) || 0 })} style={inputStyle}/>
          <select value={row.unit} onChange={e => onChange({ unit: e.target.value })}
            disabled={!row.ingredient_id} style={{ ...inputStyle, appearance: 'auto' }}>
            {!row.unit && <option value="">—</option>}
            {unitOpts.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}
          </select>
          <input placeholder="prep (e.g. chopped)" value={row.prep ?? ''}
            onChange={e => onChange({ prep: e.target.value })}
            style={{ ...inputStyle, gridColumn: mobile ? '1 / -1' : 'auto' }}/>
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontFamily: theme.sans, fontSize: 12, color: theme.inkSoft, whiteSpace: 'nowrap',
          }}>
            <input type="checkbox" checked={row.optional} onChange={e => onChange({ optional: e.target.checked })}/>
            Optional
          </label>
        </div>
      )}
    </div>
  )
}

function Stepper({ value, disabled, onChange }: { value: number; disabled?: boolean; onChange: (n: number) => void }) {
  const btn: React.CSSProperties = {
    all: 'unset', cursor: disabled ? 'default' : 'pointer',
    width: 30, height: 30, display: 'grid', placeItems: 'center',
    border: `1px solid ${theme.lineStrong}`, borderRadius: theme.radius,
    color: disabled ? theme.muted : theme.ink, fontFamily: theme.sans, fontSize: 16, fontWeight: 600,
  }
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, opacity: disabled ? 0.5 : 1 }}>
      <button style={btn} disabled={disabled} onClick={() => onChange(Math.max(1, value - 1))}>−</button>
      <span style={{ fontFamily: theme.serif, fontSize: 18, fontWeight: theme.headWeight, color: theme.ink, minWidth: 22, textAlign: 'center' }}>
        {value || '—'}
      </span>
      <button style={btn} disabled={disabled} onClick={() => onChange(value + 1)}>+</button>
    </div>
  )
}

function Label({ children }: { children: React.ReactNode }) {
  return <div style={{
    fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.4, fontWeight: 700,
    textTransform: 'uppercase', color: theme.muted, marginBottom: 6,
  }}>{children}</div>
}

const inputStyle: React.CSSProperties = {
  all: 'unset', boxSizing: 'border-box', width: '100%',
  padding: '7px 10px', borderRadius: theme.radius,
  border: `1px solid ${theme.lineStrong}`, background: theme.surface,
  fontFamily: theme.sans, fontSize: 13, color: theme.ink,
}

const ghostBtn: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '4px 10px', borderRadius: theme.radius,
  fontFamily: theme.sans, fontSize: 11, fontWeight: 600, letterSpacing: 0.4,
  textTransform: 'uppercase', color: theme.ink, border: `1px solid ${theme.lineStrong}`,
}

const iconBtn: React.CSSProperties = {
  all: 'unset', cursor: 'pointer', padding: 6, borderRadius: theme.radius,
  color: theme.inkSoft, display: 'inline-flex',
}
